/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";
import { v4 as uuid } from "uuid";
import ConditionRule from "./ConditionRule";

import { findRule, stripTags, addTags } from "./helpers";
import {
  ConditionObjectData,
  ConditionRuleData,
  RuleBuilderProps,
  DayOfWeekRuleConditionData,
  DicomRuleConditionData,
  StudyDescriptionConditionRule,
  DroppedConditionData,
  TimeOfDayRuleConditionData,
  ConditionError,
  UntaggedConditionRuleData,
  HL7ServiceRuleConditionData,
  DynamicRuleConditionData,
  OrganisationConditionRule,
  ScriptRuleConditionData,
  ModalityConditionRule,
  SiteConditionRule,
  ReportingPriorityConditionRule,
  BodyPartConditionRule,
  GenderConditionRule,
  ExamCodeConditionRule,
  AgeRangeConditionRule,
  PatientClassRule,
} from "../../types/rule";
import Validator from "./validator";

// @ts-ignore
const RuleBuilder = forwardRef((props: RuleBuilderProps, ref) => {
  const {
    initialValue,
    disabled,
    ruleTypes,
    isDark,
    organisationOptions = [],
    siteOptions = [],
    modalityOptions = [],
    patientClassOptions = [],
    showModalityOperator,
    reportPriorityOptions = [],
    genderOptions = [],
    bodyPartOptions = [],
    examCodeOptions = [],
    onHandleScriptModal,
  }: RuleBuilderProps = props;

  const [root, setRoot] = useState<ConditionRuleData>({
    id: uuid(),
    scope: "rules",
    operator: "AND",
    conditions: [],
    conditionRules: [],
  } as ConditionRuleData);
  const [errors, setErrors] = useState<ConditionError[]>([]);

  const getInitialValue = (
    init: UntaggedConditionRuleData
  ): ConditionRuleData => {
    if (init) {
      const initialValueCopy: ConditionRuleData = JSON.parse(
        JSON.stringify(init)
      );
      addTags(initialValueCopy);
      return initialValueCopy;
    }
    return {
      id: uuid(),
      scope: "rules",
      operator: "AND",
      conditions: [],
      conditionRules: [],
    };
  };

  useEffect(() => {
    setRoot(getInitialValue(initialValue as UntaggedConditionRuleData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  // Condition Group based operation goes here
  const createConditionRule = (parentId: string) => {
    const conditionRule = {
      id: uuid(),
      scope: "rules",
      operator: "AND",
      conditions: [],
      conditionRules: [],
    };

    const rootCopy = JSON.parse(JSON.stringify(root));
    const parent = findRule(parentId, rootCopy);
    if (!parent) return;
    parent.conditionRules.push(conditionRule as ConditionRuleData);
    setRoot(rootCopy);
  };

  const onRemoveConditionRule = (conditionId: string, parentId: string) => {
    const rootCopy = JSON.parse(JSON.stringify(root));
    const parent = findRule(parentId, rootCopy);
    if (!parent) return;
    const index = parent.conditionRules.findIndex(
      (x: ConditionRuleData) => x.id === conditionId
    );
    parent.conditionRules.splice(index, 1);
    setRoot(rootCopy);
  };
  const createCondition = (parentId: string) => {
    const condition = {
      type: "",
      id: uuid(),
      scope: "conditions",
    };

    const rootCopy = JSON.parse(JSON.stringify(root));

    const parent = findRule(parentId, rootCopy);
    if (!parent) return;
    parent.conditions.push(condition as ConditionObjectData);
    setRoot(rootCopy);
  };

  const onRemoveCondition = (id: string, parentId: string) => {
    const rootCopy = JSON.parse(JSON.stringify(root));
    const parent = findRule(parentId, rootCopy);
    if (!parent) return;
    const index = parent.conditions.findIndex(
      (x: ConditionObjectData) => x.id === id
    );
    // delete errorsCopy[id];
    parent.conditions.splice(index, 1);
    setRoot(rootCopy);
  };
  const onValueChangeHandler = (
    value:
      | DicomRuleConditionData
      | StudyDescriptionConditionRule
      | DayOfWeekRuleConditionData
      | TimeOfDayRuleConditionData
      | HL7ServiceRuleConditionData
      | DynamicRuleConditionData
      | OrganisationConditionRule
      | SiteConditionRule
      | ScriptRuleConditionData
      | ModalityConditionRule
      | ReportingPriorityConditionRule
      | PatientClassRule
      | BodyPartConditionRule
      | GenderConditionRule
      | ExamCodeConditionRule
      | AgeRangeConditionRule,
    parentId: string
  ) => {
    const rootCopy = JSON.parse(JSON.stringify(root));
    const parent = findRule(parentId, rootCopy);
    if (!parent) return;

    // update specific condition;
    const index = parent.conditions.findIndex(
      (x: ConditionObjectData) => x.id === value.id
    );
    parent.conditions.splice(index, 1, value as ConditionObjectData);
    setRoot(rootCopy);
  };

  const onBooleanSelect = (id: string, value: string) => {
    const rootCopy = JSON.parse(JSON.stringify(root));
    const rule = findRule(id, rootCopy);
    // @ts-ignore
    Object.assign(rule, { operator: value });
    setRoot(rootCopy);
  };

  const onDropCondition = (
    targetGroupId: string,
    condition: string,
    targetIndex: number
  ) => {
    const draggedObject: DroppedConditionData = JSON.parse(condition);

    const { value, parentId } = draggedObject;
    const rootCopy = JSON.parse(JSON.stringify(root));

    if (value.scope === "rules") {
      // Add new copy to rules
      const newRule = { ...value, id: uuid() };
      const targetParent = findRule(targetGroupId, rootCopy);
      const previousParent = findRule(parentId, rootCopy);
      if (!targetParent || !previousParent) return;

      targetParent.conditionRules.unshift(newRule);
      // Remove item from old parent
      previousParent.conditionRules = previousParent.conditionRules.filter(
        (x: ConditionRuleData) => x.id !== value.id
      );
    }

    if (value.scope === "conditions") {
      const newRule = { ...value, id: uuid() };

      const targetParent = findRule(targetGroupId, rootCopy);
      const previousParent = findRule(parentId, rootCopy);
      if (!targetParent || !previousParent) return;

      // Add new item in new parent;
      targetParent.conditions.splice(targetIndex, 0, newRule);

      // Remove item from old parent
      previousParent.conditions = previousParent.conditions.filter(
        (x: ConditionObjectData) => x.id !== value.id
      );
    }
    setRoot(rootCopy);
  };

  const onDropConditionRule = (
    targetGroupId: string,
    condition: string,
    targetIndex: number
  ) => {
    const draggedObject: DroppedConditionData = JSON.parse(condition);
    const { value, parentId } = draggedObject;
    const rootCopy = JSON.parse(JSON.stringify(root));

    if (draggedObject.value.scope === "rules") {
      // Add new copy to rules
      const newRule = { ...value, id: uuid() };
      const targetParent = findRule(targetGroupId, rootCopy);
      const previousParent = findRule(parentId, rootCopy);
      if (!targetParent || !previousParent) return;

      // Add new item in new parent
      targetParent.conditionRules.splice(
        targetIndex,
        0,
        newRule as ConditionRuleData
      );

      // Remove item from old parent
      previousParent.conditionRules = previousParent.conditionRules.filter(
        (x: ConditionRuleData) => x.id !== value.id
      );
    }

    if (draggedObject.value.scope === "conditions") {
      const newRule = { ...value, id: uuid() };

      const targetParent = findRule(targetGroupId, rootCopy);
      const previousParent = findRule(parentId, rootCopy);
      if (!targetParent || !previousParent) return;

      // Add new item in new parent
      targetParent.conditions.push(newRule as ConditionObjectData); // = [...targetParent.conditions, newRule];

      // Remove item from old parent
      previousParent.conditions = previousParent.conditions.filter(
        (x: ConditionObjectData) => x.id !== value.id
      );
    }
    setRoot(rootCopy);
  };

  useImperativeHandle(ref, () => ({
    validate() {
      const rootCopy = JSON.parse(JSON.stringify(root));
      const newErrors = new Validator().validate(rootCopy);
      setErrors(newErrors);
      stripTags(rootCopy);
      return {
        rule: rootCopy,
        errors: newErrors,
      };
    },
    getData() {
      return JSON.parse(JSON.stringify(root));
    },
  }));

  return (
    <div className="rule-builder">
      <ConditionRule
        organisationOptions={organisationOptions}
        siteOptions={siteOptions}
        onHandleScriptModal={onHandleScriptModal}
        modalityOptions={modalityOptions}
        patientClassOptions={patientClassOptions}
        showModalityOperator={showModalityOperator}
        reportPriorityOptions={reportPriorityOptions}
        genderOptions={genderOptions}
        bodyPartOptions={bodyPartOptions}
        examCodeOptions={examCodeOptions}
        isDark={isDark}
        disabled={disabled}
        ruleTypes={ruleTypes}
        id={root?.id}
        errors={errors}
        createCondition={createCondition}
        onRemoveCondition={onRemoveCondition}
        createConditionRule={createConditionRule}
        onRemoveConditionRule={onRemoveConditionRule}
        onBooleanSelect={onBooleanSelect}
        parentId={null}
        onValueChangeHandler={onValueChangeHandler}
        onDropCondition={onDropCondition}
        onDropConditionRule={onDropConditionRule}
        value={root}
      />
    </div>
  );
});

export default RuleBuilder;
